import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/styles';
import {
  FunctionComponent,
  memo,
  useEffect,
  ReactNode,
  createRef,
} from 'react';
import {
  useManageContext,
  ManageContexWrapper,
} from './contexts/ManageContext/ManageContext';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  withRouter,
} from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider/AuthProvider';
import ConfigProvider from './eliot-components/src/ConfigProvider/ConfigProvider';
import Dashboard from './pages/Dashboard/Dashboard';
import Device from './pages/Device/Device';
import Devices from './pages/Devices/Devices';
import ForgotPassword from './eliot-components/src/ForgotPassword/ForgotPassword';
import Login from './eliot-components/src/Login/Login';
import TargetSystems from './pages/TargetSystems/TargetSystems';
import Page from './Layout/Page';
import BarcodeScanner from './components/Barcode/BarcodeScanner';
import { useUpdateDeviceStateQueue } from './hooks';
import { useConfig } from './eliot-components/src/ConfigProvider/hooks';
import { useThemeWithDarkMode } from './eliot-components/src/theme';
import { useSwipeableNavigation } from './utils/swipeable';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Button } from '@material-ui/core';
import ErrorPage from './eliot-components/src/Error/ErrorPage';
import Auth from '@aws-amplify/auth';
import GlobalNoRolesPage from './pages/GlobalNoRoles/GlobalNoRolesPage';
import AuthChangePage from './eliot-components/src/Error/AuthChangePage';

const RouteListener = withRouter(({ history, children }) => {
  const { dispatch } = useManageContext();
  useSwipeableNavigation();

  useEffect(() => {
    return history.listen(() => {
      dispatch({ type: 'sidebar-hidden' });
    });
  }, [dispatch, history]);

  return <>{children}</>;
});

const AppRefreshData = () => {
  useUpdateDeviceStateQueue();
  return <></>;
};

const M2M = (props: { children: ReactNode }) => {
  const { children } = props;
  const { isM2MFeatureEnabled } = useConfig();

  if (isM2MFeatureEnabled) {
    return <>{children}</>;
  }

  return null;
};

const App: FunctionComponent = memo(() => {
  const { state, dispatch } = useManageContext();
  const { enqueueSnackbar } = useSnackbar();

  if (!!state.isTransferMessageVisible) {
    enqueueSnackbar('Device successfully transferred', { variant: 'success' });
    dispatch({ type: 'transfer-ownership', data: false });
  }

  return (
    <>
      <CssBaseline />
      <M2M>
        <AppRefreshData />
      </M2M>
      <Router>
        <RouteListener />

        <Switch>
          <Route path="/" exact render={() => <Redirect to="/dashboard" />} />
          <Route path="/login" component={() => <Login appName="Manage" />} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route
            path="/dashboard"
            render={() => (
              <Page name="dashboard">
                <AuthProvider>
                  <Dashboard />
                </AuthProvider>
              </Page>
            )}
          />
          <Route
            path="/devices"
            exact
            render={() => (
              <Page name="devices">
                <AuthProvider>
                  <Devices />
                </AuthProvider>
              </Page>
            )}
          />
          <Route
            path="/devices/:deviceID"
            render={() => (
              <Page name="device-details">
                <AuthProvider>
                  <Device />
                </AuthProvider>
              </Page>
            )}
          />
          <Route
            path="/webhooks"
            render={() => (
              <Page name="target-systems">
                <AuthProvider>
                  <TargetSystems />
                </AuthProvider>
              </Page>
            )}
          />
          <Route
            path="/error"
            render={() => {
              if (window.location.search.includes('authchange=true')) {
                return (
                  <AuthChangePage
                    onLogout={(callback) => {
                      Auth.signOut()
                        .then(() => {
                          window.localStorage.clear();
                          window.location.replace('/login');
                          callback();
                        })
                        .catch(() => {
                          window.localStorage.clear();
                          window.location.replace('/login');
                          callback();
                        });
                    }}
                  />
                );
              }
              return (
                <ErrorPage
                  buttonLink="/dashboard"
                  buttonText="Go to Dashboard"
                />
              );
            }}
          />
          <Route
            path="/logout"
            render={async () => {
              try {
                await Auth.signOut({ global: true });
              } catch (e) {
                await Auth.signOut();
              }
              setTimeout(() => {
                window.location.replace('/login');
              });
              return <></>;
            }}
          />
          <Route
            path="/global"
            render={() => {
              const params = new URLSearchParams(window.location.search);
              return (
                <Page name="global">
                  <GlobalNoRolesPage userID={params.get('id') || ''} />
                </Page>
              );
            }}
          />
          <Route
            path="*"
            render={() => (
              <ErrorPage
                error={'Page Not Found'}
                buttonLink="/dashboard"
                buttonText="Go to Dashboard"
              />
            )}
          />
        </Switch>
      </Router>
      <BarcodeScanner />
    </>
  );
});

const AppWithContexts = () => {
  const themeState = useThemeWithDarkMode();
  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = (key: any) => () => {
    if (notistackRef && notistackRef.current) {
      //@ts-ignore
      notistackRef.current.closeSnackbar(key);
    }
  };

  return (
    <ThemeProvider theme={themeState.theme}>
      <SnackbarProvider
        maxSnack={3}
        preventDuplicate={true}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        ref={notistackRef}
        action={(key) => (
          <Button onClick={onClickDismiss(key)} style={{ color: 'white' }}>
            Dismiss
          </Button>
        )}
      >
        <ManageContexWrapper
          toggleDarkMode={themeState.toggleDarkMode}
          isDarkModeActive={themeState.isDarkModeActive}
        >
          <ConfigProvider>
            <App />
          </ConfigProvider>
        </ManageContexWrapper>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default AppWithContexts;
